import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  RefreshCw,
  ExternalLink,
  Copy,
  Check,
  Pencil,
  Trash2,
  BarChart3,
  Link2,
  ToggleLeft,
  ToggleRight,
  Smartphone,
  Monitor,
  Eye,
  HelpCircle,
  X,
} from 'lucide-react';
import {
  APP_LINK_DEFAULTS,
  createLink,
  deleteLink,
  emptyLinkForm,
  getLinkAnalytics,
  linkToForm,
  listLinks,
  marketingShortLinkUrl,
  toggleLinkActive,
  updateLink,
} from '../../services/urlManageService';

const typeBadgeClass = (type) =>
  type === 'app' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700';

const deviceMeta = {
  ios: { label: 'iOS', color: '#1B4F9C', Icon: Smartphone },
  android: { label: 'Android', color: '#0F766E', Icon: Smartphone },
  desktop: { label: 'Desktop', color: '#475569', Icon: Monitor },
  preview: { label: 'Link preview', color: '#B45309', Icon: Eye },
  unknown: { label: 'Unknown', color: '#94A3B8', Icon: HelpCircle },
  other: { label: 'Unknown', color: '#94A3B8', Icon: HelpCircle },
};

const formatDay = (day) => {
  if (!day || day === 'unknown') return '—';
  try {
    return new Date(`${day}T12:00:00`).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return day;
  }
};

const UrlManage = () => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [analyticsLink, setAnalyticsLink] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [form, setForm] = useState(emptyLinkForm('app'));
  const [formError, setFormError] = useState('');
  const [copiedCode, setCopiedCode] = useState('');
  const currentUser = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');

  const { data: links = [], isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['urlManageLinks'],
    queryFn: listLinks,
  });

  const {
    data: analytics,
    isLoading: analyticsLoading,
    isError: analyticsIsError,
    error: analyticsError,
  } = useQuery({
    queryKey: ['urlManageAnalytics', analyticsLink?.id],
    queryFn: () => getLinkAnalytics(analyticsLink.id),
    enabled: Boolean(analyticsLink?.id),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        created_by: currentUser?.email || currentUser?.name || null,
      };
      if (editingLink?.id) {
        return updateLink(editingLink.id, payload);
      }
      return createLink(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['urlManageLinks']);
      setModalOpen(false);
      setEditingLink(null);
      setForm(emptyLinkForm('app'));
      setFormError('');
    },
    onError: (error) => {
      setFormError(error.message || 'Failed to save link');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }) => toggleLinkActive(id, isActive),
    onSuccess: () => queryClient.invalidateQueries(['urlManageLinks']),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteLink(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries(['urlManageLinks']);
      setAnalyticsLink((current) => (current?.id === deletedId ? null : current));
    },
  });

  const stats = useMemo(() => {
    const totalClicks = links.reduce((sum, link) => sum + (Number(link.click_count) || 0), 0);
    const activeCount = links.filter((link) => link.is_active).length;
    return { totalLinks: links.length, activeCount, totalClicks };
  }, [links]);

  const openCreateModal = (type = 'app') => {
    setEditingLink(null);
    setForm(emptyLinkForm(type));
    setFormError('');
    setModalOpen(true);
  };

  const openEditModal = (link) => {
    setEditingLink(link);
    setForm(linkToForm(link));
    setFormError('');
    setModalOpen(true);
  };

  const handleCopy = async (code) => {
    const url = marketingShortLinkUrl(code);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 1500);
    } catch {
      window.prompt('Copy this URL:', url);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Link2 size={24} className="text-[#1B4F9C]" />
            URL Manage
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Smart app links and promo short URLs on app.roadserve.in/go/
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm hover:bg-gray-50"
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => openCreateModal('promo')}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 text-sm hover:bg-purple-100"
          >
            <Plus size={16} />
            Promo Link
          </button>
          <button
            type="button"
            onClick={() => openCreateModal('app')}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1B4F9C] text-white text-sm hover:bg-[#153E7A]"
          >
            <Plus size={16} />
            App Smart Link
          </button>
        </div>
      </div>

      {isError ? (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error?.message || 'Could not load short links. Log out, log in again, then Refresh.'}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Total links</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.totalLinks}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Active links</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.activeCount}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Total clicks</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.totalClicks}</p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Short URL</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Clicks</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Loading links...
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-red-600">
                    {error?.message || 'Failed to load links'}
                  </td>
                </tr>
              ) : links.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No links yet. Create an App Smart Link or Promo Link.
                  </td>
                </tr>
              ) : (
                links.map((link) => {
                  const shortUrl = marketingShortLinkUrl(link.code);
                  return (
                    <tr key={link.id} className="border-t border-gray-100 hover:bg-gray-50/70">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{link.title}</div>
                        <div className="text-xs text-gray-400">/{link.code}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <a
                            href={shortUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#1B4F9C] hover:underline truncate max-w-[220px]"
                          >
                            {shortUrl.replace('https://', '')}
                          </a>
                          <button
                            type="button"
                            onClick={() => handleCopy(link.code)}
                            className="p-1 rounded hover:bg-gray-100 text-gray-500"
                            title="Copy URL"
                          >
                            {copiedCode === link.code ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                          </button>
                          <a
                            href={shortUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1 rounded hover:bg-gray-100 text-gray-500"
                            title="Open"
                          >
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${typeBadgeClass(link.type)}`}>
                          {link.type === 'app' ? 'App' : 'Promo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{link.click_count || 0}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggleMutation.mutate({ id: link.id, isActive: !link.is_active })}
                          className="inline-flex items-center gap-1 text-xs"
                          title={link.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {link.is_active ? (
                            <>
                              <ToggleRight size={18} className="text-green-600" />
                              <span className="text-green-700">Active</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft size={18} className="text-gray-400" />
                              <span className="text-gray-500">Inactive</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setAnalyticsLink(link)}
                            className="p-2 rounded-lg hover:bg-blue-50 text-[#1B4F9C]"
                            title="Analytics"
                          >
                            <BarChart3 size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditModal(link)}
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                            title="Edit"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Delete link "${link.title}"?`)) {
                                deleteMutation.mutate(link.id);
                              }
                            }}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingLink ? 'Edit Link' : form.type === 'app' ? 'Create App Smart Link' : 'Create Promo Link'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setModalOpen(false);
                  setEditingLink(null);
                  setFormError('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form
              className="p-6 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                saveMutation.mutate();
              }}
            >
              {formError && (
                <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Title</span>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => updateField('title', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="Summer campaign"
                    required
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Custom code (optional)</span>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => updateField('code', e.target.value.toLowerCase())}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="Auto-generated if blank"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Type</span>
                  <select
                    value={form.type}
                    onChange={(e) => updateField('type', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    disabled={Boolean(editingLink)}
                  >
                    <option value="app">App smart link</option>
                    <option value="promo">Promo link</option>
                  </select>
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium text-gray-700">Preview URL</span>
                  <input
                    type="text"
                    readOnly
                    value={form.code ? marketingShortLinkUrl(form.code) : 'Code will be generated on save'}
                    className="mt-1 w-full rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-600"
                  />
                </label>
              </div>

              {form.type === 'promo' ? (
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Destination URL</span>
                  <input
                    type="url"
                    value={form.default_url}
                    onChange={(e) => updateField('default_url', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="https://app.roadserve.in/membership"
                    required
                  />
                </label>
              ) : (
                <div className="space-y-4 rounded-xl border border-blue-100 bg-blue-50/40 p-4">
                  <p className="text-sm font-medium text-[#1B4F9C]">App redirect settings</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-sm text-gray-700">iOS app scheme</span>
                      <input
                        type="text"
                        value={form.ios_scheme_url}
                        onChange={(e) => updateField('ios_scheme_url', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-700">iOS fallback URL</span>
                      <input
                        type="url"
                        value={form.ios_fallback_url}
                        onChange={(e) => updateField('ios_fallback_url', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-700">Android Play Store URL</span>
                      <input
                        type="url"
                        value={form.android_url}
                        onChange={(e) => updateField('android_url', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-700">Android scheme (optional)</span>
                      <input
                        type="text"
                        value={form.android_scheme_url}
                        onChange={(e) => updateField('android_scheme_url', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        placeholder="Leave blank for Play Store only"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-700">Android package</span>
                      <input
                        type="text"
                        value={form.android_package}
                        onChange={(e) => updateField('android_package', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm text-gray-700">Desktop URL</span>
                      <input
                        type="url"
                        value={form.desktop_url}
                        onChange={(e) => updateField('desktop_url', e.target.value)}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Defaults: iOS tries {APP_LINK_DEFAULTS.ios_scheme_url}, Android goes to Play Store, desktop opens referral page.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">UTM source</span>
                  <input
                    type="text"
                    value={form.utm_source}
                    onChange={(e) => updateField('utm_source', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="whatsapp"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">UTM medium</span>
                  <input
                    type="text"
                    value={form.utm_medium}
                    onChange={(e) => updateField('utm_medium', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="social"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">UTM campaign</span>
                  <input
                    type="text"
                    value={form.utm_campaign}
                    onChange={(e) => updateField('utm_campaign', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="summer2026"
                  />
                </label>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => updateField('is_active', e.target.checked)}
                />
                Active
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    setEditingLink(null);
                    setFormError('');
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isLoading}
                  className="px-4 py-2 rounded-lg bg-[#1B4F9C] text-white text-sm hover:bg-[#153E7A] disabled:opacity-60"
                >
                  {saveMutation.isLoading ? 'Saving...' : editingLink ? 'Update Link' : 'Create Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {analyticsLink && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-950/55 backdrop-blur-sm">
          <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#F7F8FA] shadow-2xl border border-white/40">
            <div className="sticky top-0 z-10 px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-b from-[#1B4F9C] to-[#163F7C] text-white">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-blue-100/90 font-medium">Link analytics</p>
                  <h2 className="text-xl font-semibold truncate mt-0.5">{analyticsLink.title || 'Untitled'}</h2>
                  <p className="text-sm text-blue-100/90 mt-1 font-mono">/{analyticsLink.code}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setAnalyticsLink(null)}
                  className="shrink-0 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="p-5 sm:p-6 space-y-5">
              {analyticsLoading ? (
                <p className="text-sm text-slate-500 py-8 text-center">Loading analytics…</p>
              ) : analyticsIsError ? (
                <p className="text-sm text-red-700 py-8 text-center">
                  {analyticsError?.message
                    || 'Could not load analytics. Re-run supabase/fix-url-manage-staff-rls.sql, then log out and log in.'}
                </p>
              ) : (
                <>
                  {(() => {
                    const total = analytics?.total || 0;
                    const human = analytics?.human ?? total;
                    const preview = analytics?.preview || 0;
                    const byOs = analytics?.byOs || {};
                    const maxDay = Math.max(1, ...(analytics?.byDay || []).map((d) => d.count));
                    const deviceOrder = ['ios', 'android', 'desktop', 'preview', 'unknown'];

                    return (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-sm">
                            <p className="text-xs text-slate-500">Logged opens</p>
                            <p className="text-3xl font-semibold text-slate-900 tracking-tight mt-1">{total}</p>
                            <p className="text-[11px] text-slate-400 mt-1">Har hit DB mein record hota hai</p>
                          </div>
                          <div className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-sm">
                            <p className="text-xs text-slate-500">Likely real users</p>
                            <p className="text-3xl font-semibold text-[#1B4F9C] tracking-tight mt-1">{human}</p>
                            <p className="text-[11px] text-slate-400 mt-1">
                              {preview > 0 ? `−${preview} WhatsApp/FB previews` : 'No preview bots detected'}
                            </p>
                          </div>
                        </div>

                        <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3 text-xs text-amber-900 leading-relaxed">
                          <strong className="font-semibold">“Other / Link preview”</strong> bhi actual logged hits hain —
                          fake nahi. Jab link WhatsApp/Facebook pe share hota hai, unka server pehle page fetch karta hai
                          (unfurl). Woh click human open nahi hota, isliye alag dikhaya hai.
                        </div>

                        <div className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-sm space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-slate-900">By device</h3>
                            <BarChart3 size={16} className="text-slate-400" />
                          </div>
                          <div className="space-y-2.5">
                            {deviceOrder.map((key) => {
                              const count = byOs[key] || 0;
                              const pct = total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
                              const meta = deviceMeta[key];
                              const Icon = meta.Icon;
                              return (
                                <div key={key}>
                                  <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="inline-flex items-center gap-1.5 text-slate-600">
                                      <Icon size={13} style={{ color: meta.color }} />
                                      {meta.label}
                                    </span>
                                    <span className="font-medium text-slate-800 tabular-nums">
                                      {count} <span className="text-slate-400 font-normal">({pct}%)</span>
                                    </span>
                                  </div>
                                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                    <div
                                      className="h-full rounded-full transition-all duration-500"
                                      style={{
                                        width: `${total ? (count / total) * 100 : 0}%`,
                                        background: meta.color,
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-sm">
                          <h3 className="text-sm font-semibold text-slate-900 mb-3">Clicks by day</h3>
                          {analytics?.byDay?.length ? (
                            <div className="space-y-2">
                              {analytics.byDay.slice(0, 14).map((row) => (
                                <div key={row.day} className="flex items-center gap-3 text-sm">
                                  <span className="w-24 shrink-0 text-xs text-slate-500 tabular-nums">
                                    {formatDay(row.day)}
                                  </span>
                                  <div className="flex-1 h-7 rounded-lg bg-slate-100 overflow-hidden relative">
                                    <div
                                      className="absolute inset-y-0 left-0 bg-[#1B4F9C]/85 rounded-lg transition-all duration-500"
                                      style={{ width: `${(row.count / maxDay) * 100}%` }}
                                    />
                                    <span className="relative z-[1] px-2 text-xs font-medium text-white drop-shadow-sm leading-7">
                                      {row.count}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500">No clicks recorded yet.</p>
                          )}
                        </div>

                        <div className="rounded-2xl bg-white border border-slate-200/80 overflow-hidden shadow-sm">
                          <div className="px-4 py-3 border-b border-slate-100">
                            <h3 className="text-sm font-semibold text-slate-900">Recent activity</h3>
                          </div>
                          {analytics?.recent?.length ? (
                            <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                              {analytics.recent.map((row) => {
                                const kind = row.device || row.os || 'unknown';
                                const meta = deviceMeta[kind] || deviceMeta.unknown;
                                return (
                                  <div key={row.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm">
                                    <span className="text-xs text-slate-500 whitespace-nowrap tabular-nums sm:w-40 shrink-0">
                                      {row.occurred_at ? new Date(row.occurred_at).toLocaleString('en-IN') : '—'}
                                    </span>
                                    <span
                                      className="inline-flex self-start items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
                                      style={{ background: `${meta.color}18`, color: meta.color }}
                                    >
                                      {meta.label}
                                    </span>
                                    <span className="text-xs text-slate-600 truncate min-w-0" title={row.resolved_target || ''}>
                                      {row.resolved_target || '—'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 px-4 py-6">No recent clicks.</p>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UrlManage;
